import * as React from "react";
import { cn } from "@/lib/utils";

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  icon?: React.ReactNode;
  label?: string;
  description?: string;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    { className, type, icon, label, description, id, ...props },
    ref,
  ) => {
    const generatedId = React.useId();
    const inputId = id ?? (label ? generatedId : undefined);

    return (
      <div className="flex flex-col gap-1.5 w-full">
        {label && (
          <label
            htmlFor={inputId}
            className="font-medium text-sm leading-none text-zinc-900"
          >
            {label}
          </label>
        )}
        <div className="relative flex items-center w-full">
          {icon && (
            <div className="absolute left-3 flex items-center pointer-events-none">
              {icon}
            </div>
          )}
          <input
            ref={ref}
            id={inputId}
            type={type}
            data-slot="input"
            className={cn(
              "flex h-10 w-full min-w-0 rounded-md border bg-white px-4 py-1 text-sm text-black/45",
              "shadow-xs transition-[color,box-shadow] outline-none",
              "border-base-border",
              "placeholder:text-black/45",
              "focus-visible:ring-[3px] focus-visible:ring-custom-outline/50 focus-visible:border-custom-outline",
              "disabled:pointer-events-none disabled:cursor-not-allowed disabled:text-base-border",
              "aria-invalid:border-custom-destructive-dark",
              "aria-invalid:focus-visible:ring-[3px] aria-invalid:focus-visible:ring-custom-destructive-dark/50 aria-invalid:focus-visible:border-custom-destructive-dark",
              icon && "pl-10",
              className,
            )}
            {...props}
          />
        </div>
        {description && (
          <p className="text-sm leading-[1.25rem] text-black/45">
            {description}
          </p>
        )}
      </div>
    );
  },
);

Input.displayName = "Input";

export { Input };
